import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { itemsService } from '../../services/itemsService';

export interface SearchableItem {
  itemNo: string;
  description1?: string;
  itemUnitCode?: string;
  stdLocationName?: string;
}

interface ItemSearchSelectProps {
  value: string;
  onChange: (itemNo: string, item?: SearchableItem) => void;
  placeholder?: string;
  /** Optional display label for the currently selected value (shown when not searching) */
  selectedLabel?: string;
}

export const ItemSearchSelect: React.FC<ItemSearchSelectProps> = ({ value, onChange, placeholder = 'Søk vare...', selectedLabel }) => {
  const [searchText, setSearchText] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [searchResults, setSearchResults] = useState<SearchableItem[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const performSearch = useCallback(async (term: string) => {
    if (!term.trim() || term.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      const results = await itemsService.searchCurrentItems(term);
      setSearchResults(results.map(item => ({
        itemNo: item.itemNo,
        description1: item.description1,
        itemUnitCode: item.itemUnitCode,
        stdLocationName: item.stdLocationName
      })));
    } catch (err) {
      console.error('Item search failed:', err);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchResults]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearchText('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (term: string) => {
    setSearchText(term);
    if (!isOpen) setIsOpen(true);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      performSearch(term);
    }, 300);
  };

  const handleSelect = useCallback((item: SearchableItem) => {
    onChange(item.itemNo, item);
    setIsOpen(false);
    setSearchText('');
    setSearchResults([]);
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => Math.min(prev + 1, searchResults.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (searchResults[highlightedIndex]) {
          handleSelect(searchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchText('');
        break;
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchText('');
    setSearchResults([]);
    setIsOpen(false);
  };

  const dropdownContent = isOpen && dropdownPos && ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className="max-h-60 overflow-auto bg-white border border-gray-300 rounded-lg shadow-lg"
      style={{
        position: 'absolute',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 9999
      }}
    >
      {loading ? (
        <div className="px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Søker...
        </div>
      ) : searchText.trim().length < 2 ? (
        <div className="px-3 py-2 text-sm text-gray-500">Skriv minst 2 tegn for å søke</div>
      ) : searchResults.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">Ingen treff</div>
      ) : (
        searchResults.map((item, index) => (
          <div
            key={item.itemNo}
            className={`px-3 py-1.5 text-sm cursor-pointer ${
              index === highlightedIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
            } ${item.itemNo === value ? 'font-semibold' : ''}`}
            onMouseEnter={() => setHighlightedIndex(index)}
            onMouseDown={(e) => {
              e.preventDefault();
              handleSelect(item);
            }}
          >
            <span className="font-medium">{item.itemNo}</span>
            <span className="text-gray-500 ml-1">- {item.description1 || ''}</span>
          </div>
        ))
      )}
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchText : (selectedLabel || value || '')}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => {
            setIsOpen(true);
            setSearchText('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full border border-gray-300 rounded px-2 py-1 pl-7 pr-7 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {dropdownContent}
    </div>
  );
};
